import { FC, memo } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch, useResponsive } from '../../../../hooks';
import { detectStore, useMobileAppLink } from '../../utils';
import { titles } from './constants';
import { Text, Button, message, Dialog, VStack } from '../../components-v4';
import { useV4Theme } from '../../components-v4/theme';
import {
  getShowMetersResultModalState,
  getMetersCheckRequestLink,
} from '../../selectors';
import { setShowMetersResultModal } from '../../request-single-page-slice';
import {
  SuccessCircle,
  ButtonsWrapper,
  MobileHeader,
  MobileHeaderClose,
  StyledDialogContent,
  StyledDialogPanel,
} from './meters-result-modal.styled';

import { ReactComponent as CopyIcon } from '../../assets/clone.svg';
import { ReactComponent as MobileIcon } from '../../assets/mobile.svg';
import { ReactComponent as SuccessIcon } from '../../assets/success.svg';

export const MetersResultModal: FC = memo(() => {
  const theme = useV4Theme();
  const dispatch = useAppDispatch();
  const { viewport } = useResponsive();
  const modalType = useSelector(getShowMetersResultModalState);
  const requestLink = useSelector(getMetersCheckRequestLink);
  const store = detectStore();
  const link = useMobileAppLink(store);

  if (!modalType) {
    return null;
  }

  const handleClose = () => {
    dispatch(setShowMetersResultModal(null));
  };

  const handleCopyRequestLink = () => {
    navigator.clipboard.writeText(requestLink || '').then(() => {
      message({
        type: 'success',
        text: 'Ссылка скопирована',
      });
    });
  };

  const handleGoApp = () => {
    if (!viewport.desktop) {
      window.open(link, '_blank', 'noopener noreferrer');

      return;
    }

    window.open('https://кабинет-жителя.рф', '_blank', 'noopener noreferrer');
  };

  return (
    <Dialog.Root open={!!modalType} onOpenChange={handleClose}>
      <Dialog.Portal>
        <StyledDialogContent
          overlayStyle={{
            ...(!viewport.desktop && { backgroundColor: theme.colors.white }),
          }}
        >
          <StyledDialogPanel>
            <VStack flex={1} align="center" spacing="xs">
              {!viewport.desktop && (
                <MobileHeader>
                  <MobileHeaderClose onClick={handleClose}>
                    <Text>Закрыть</Text>
                  </MobileHeaderClose>
                </MobileHeader>
              )}

              <VStack padding={`0 ${theme.spacings.base}`} flex={1} center>
                <SuccessCircle>
                  <SuccessIcon
                    className="check-icon"
                    width={viewport.desktop ? 85 : 55}
                    height={viewport.desktop ? 68 : 44}
                  />
                </SuccessCircle>

                <Text variant={viewport.mobile ? 'heading-3' : 'heading-2'}>
                  {titles[modalType]}
                </Text>

                {modalType === 'request' && (
                  <Text
                    variant={viewport.mobile ? 'body-14' : 'body-16'}
                    color="gray.500"
                    align="center"
                  >
                    Вы можете отслеживать её статус в приложении
                    "Кабинет-жителя.рф", либо по ссылке (без авторизации)
                  </Text>
                )}
              </VStack>

              <ButtonsWrapper>
                {modalType === 'readings' && (
                  <Button
                    size="l"
                    mode="accent"
                    variant="main"
                    onClick={handleClose}
                    style={{ width: '100%' }}
                  >
                    Вернуться к заявке
                  </Button>
                )}

                {modalType === 'request' && (
                  <>
                    <Button
                      size="l"
                      mode="neutral"
                      variant="main"
                      onClick={handleCopyRequestLink}
                      leftIcon={<CopyIcon />}
                      style={{ width: '100%' }}
                    >
                      Скопировать ссылку на заявку
                    </Button>
                    <Button
                      size="l"
                      mode="accent"
                      variant="main"
                      onClick={handleGoApp}
                      leftIcon={<MobileIcon />}
                      style={{ width: '100%' }}
                    >
                      Перейти в приложение
                    </Button>
                  </>
                )}
              </ButtonsWrapper>
            </VStack>
          </StyledDialogPanel>
        </StyledDialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
});
